
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import './App.css'; // The new CSS file will style this
import { FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import DualWaveformVisualizer from './components/DualWaveformVisualizer';

import burlingtonLogo from './burlington-logo.png';

const BurlingtonLogo = () => (
  <img
    style={{ width: '200px', height: 'auto' }}
    src={burlingtonLogo}
    alt='Burlington Logo'
  />
);


function App() {
  // State Variables
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('Disconnected');
  const [isListening, setIsListening] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  // Refs for various objects
  const audioContextRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const userStreamRef = useRef(null);
  
  // Refs for streaming audio playback
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const currentAudioRef = useRef(null);

  // Function to play the next audio chunk in the queue
  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length > 0) {
      isPlayingRef.current = true;
      setIsAiSpeaking(true);
      
      const audioBlob = audioQueueRef.current.shift();
      const audioUrl = URL.createObjectURL(audioBlob);
      const newAudio = new Audio(audioUrl);
      currentAudioRef.current = newAudio;

      newAudio.play().catch(e => console.error("Error playing audio:", e));
      
      newAudio.onended = () => {
        playNextInQueue(); // Play the next one
      };
    } else {
      // Queue is empty, we're done
      isPlayingRef.current = false;
      setIsAiSpeaking(false);
      setStatus('Ready');
      currentAudioRef.current = null;
    }
  }, []);


  // Setup Socket Connection & Event Handlers
  useEffect(() => {
    const socketURL =
      process.env.NODE_ENV === 'production'
        ? undefined
        : process.env.REACT_APP_SERVER_URL || 'http://localhost:3008';

    socketRef.current = io(socketURL);

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setStatus('Connected');
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      setStatus('Disconnected');
      setIsListening(false);
      setIsAiSpeaking(false);
      isPlayingRef.current = false;
      audioQueueRef.current = [];
    });

    socketRef.current.on('transcription', (data) => {
      if (data?.text) {
        setConversation((prev) => [...prev, { role: 'user', text: data.text }]);
      }
    });

    // Listen for AI response with both text and audio
    socketRef.current.on('aiResponse', (data) => {
        if (data?.text) {
            const cleanText = data.text.replace(/<[^>]*>|<\/[^>]*>/g, '');
            setConversation((prev) => [...prev, { role: 'assistant', text: cleanText }]);
            setStatus('Assistant is speaking...');
        }
        
        if (data?.audio) {
            // Convert base64 audio to blob and play it
            const binaryString = atob(data.audio);
            const audioBytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                audioBytes[i] = binaryString.charCodeAt(i);
            }
            const audioBlob = new Blob([audioBytes], { type: 'audio/mpeg' });
            audioQueueRef.current.push(audioBlob);
            if (!isPlayingRef.current) {
                playNextInQueue();
            }
        }
    });
    
    // STREAMING FIX: The 'audioEnd' event signals the end of a response stream
    socketRef.current.on('audioEnd', () => {
        console.log("Server finished sending audio.");
        // The queue will handle setting the final status when it's empty
    });

    socketRef.current.on('error', (error) => {
      console.error('Socket error:', error);
      setIsListening(false);
      setIsAiSpeaking(false);
      isPlayingRef.current = false;
      setStatus('Error occurred');
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (userStreamRef.current) {
        userStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [playNextInQueue]);

  const startListening = async () => {
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      userStreamRef.current = stream;

      // Try to use WebM with Opus codec specifically
      let options = { mimeType: 'audio/webm;codecs=opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        console.warn('WebM Opus not supported, trying alternatives...');
        options = { mimeType: 'audio/webm' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) {
          console.warn('WebM not supported, using default');
          options = {};
        }
      }
      
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm;codecs=opus' });
        console.log('Audio blob created:', audioBlob.type, 'size:', audioBlob.size);
        await sendAudioToServer(audioBlob);
      };

      mediaRecorderRef.current.start(2000); // Send audio every 2 seconds for better transcription
      setIsListening(true);
      setStatus('Listening...');
    } catch (error) {
      console.error('Error starting recording:', error);
      setStatus('Error accessing microphone');
    }
  };

  const stopListening = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
      }
      setIsListening(false);
      setStatus('Processing...');
    }
  };

  const toggleListening = () => {
    isListening ? stopListening() : startListening();
  };

  const sendAudioToServer = async (audioBlob) => {
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = reader.result.split(',')[1];
        socketRef.current.emit('audioChunk', { audioChunk: base64Audio });
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Error sending audio to server:', error);
    }
  };

  return (
    <div className="app-container" style={{ position: 'relative', overflow: 'hidden' }}>
      {/* Burlington SVG Background */}
      <svg className="burlington-bg-svg h-full max-h-[46rem] text-brand-red-500" viewBox="0 0 110 109" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M70.34,81.76c-15,15-33.33-2.65-50.29,6L17.76,89l1.14-2.4c8.62-17-9-35.35,6-50.3,8.37-8.38,19.25-6.6,26,.12,3.3,3.3,3.28,12,.24,18.08l-.25.76.63-.38c6.08-3,15.47-2.36,18.77.93,6.72,6.72,8.49,17.6.12,26M96.1,46c-6-6-17.4-11.84-28.54-6.29l-1.16.71.47-1.4c5.56-11.13-.25-22.5-6.29-28.55C48.28-1.78,28.32-5,13,10.29c-27.39,27.39,4.87,61.05-10.9,92.15L0,106.86l4.19-2.33C35.27,88.76,68.94,121,96.33,93.62,111.67,78.29,108.41,58.34,96.1,46"></path>
      </svg>
      <header className="app-header">
        <BurlingtonLogo />
      </header>

      <main className="app-main">
        <div className="visualizer-container">
          <DualWaveformVisualizer
            userStream={userStreamRef.current}
            aiAudio={currentAudioRef.current} // Pass the currently playing audio element
            isUserSpeaking={isListening}
            isAiSpeaking={isAiSpeaking}
          />
        </div>

        <div className="controls-container">
          <div className="status-indicator">
            <div className={`status-dot ${
              isListening ? 'status-listening' : isAiSpeaking ? 'status-speaking' : isConnected ? 'status-connected' : 'status-disconnected'
            }`}></div>
            <span className="status-text">{status}</span>
          </div>

          <div className="action-area">
            <button
              onClick={toggleListening}
              disabled={!isConnected || isAiSpeaking}
              className={`mic-button ${isListening ? 'listening' : ''} ${(!isConnected || isAiSpeaking) ? 'disabled' : ''}`}
            >
              {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
            </button>
            <div className="action-text">
              <p>
                {isListening
                  ? "Listening..."
                  : isAiSpeaking
                    ? "Assistant is speaking..."
                    : "Tap the microphone to speak"}
              </p>
            </div>
          </div>
        </div>

        <div className="conversation-container">
          {conversation.length > 0 && (
            <div className="conversation-scrollbox">
              {[...conversation].reverse().map((message, index) => (
                <div key={index} className={`message-bubble ${message.role === 'user' ? 'user-bubble' : 'assistant-bubble'}`}>
                  <p className="message-sender">
                    {message.role === 'user' ? 'You' : 'Burlington Assistant'}
                  </p>
                  <p className="message-text">{message.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <footer className="app-footer">
        <div className="footer-status">
          <div className={`status-dot ${isConnected ? 'status-connected' : 'status-disconnected'}`}></div>
          <span>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
        </div>
        <p>© {new Date().getFullYear()} Burlington AI Assistant</p>
      </footer>
    </div>
  );
}

export default App;
